import { v4 as uuidv4 } from "uuid";
import { abortRequestAfter } from "./abort-request-after";
import { getApiKey } from "./addin-auth";
import { getEnv } from "./envManager";
import { encodeApplyOutcomeBatchForWire } from "./redline-wire";
import { safeFetch } from "./safe-fetch";

export const REDLINE_APPLY_OUTCOME_EVENT = "Redline Apply Outcome";
export const REDLINE_APPLY_OUTCOMES_PATH = "/redline/apply-outcomes";

export const OUTCOME_POST_TIMEOUT_MS = 15_000;

export const REDLINE_APPLY_OUTCOMES = [
    "generated",
    "selected",
    "dropped",
    "attempted",
    "applied_clean",
    "recovered_backend_repair",
    "recovered_agent_clean",
    "recovered_agent_drift",
    "range_unresolved",
    "hard_failure",
] as const;

export type RedlineApplyOutcome = (typeof REDLINE_APPLY_OUTCOMES)[number];

export const REDLINE_DROP_REASONS = [
    "duplicate_change_key",
    "missing_target_fields",
    "empty_ooxml",
] as const;

export type RedlineDropReason = (typeof REDLINE_DROP_REASONS)[number];

export const REDLINE_APPLY_INITIATED_FROM = [
    "bulk",
    "single",
    "chat_apply_all",
    "live_review",
    "execute_js",
] as const;

export type RedlineApplyInitiatedFrom = (typeof REDLINE_APPLY_INITIATED_FROM)[number];

export const REDLINE_AGENT_ROLES = [
    "chat",
    "sub_agent",
    "agent_action",
] as const;

export type RedlineAgentRole = (typeof REDLINE_AGENT_ROLES)[number];

export function isRedlineAgentRole(value: unknown): value is RedlineAgentRole {
    return typeof value === "string" && (REDLINE_AGENT_ROLES as readonly string[]).includes(value);
}

export type RedlineUserGestureSurface = "bulk" | "single" | "chat_apply_all" | "live_review";

export type RedlineApplySource =
    | { initiatedFrom: RedlineUserGestureSurface; reviewId: string | null }
    | { initiatedFrom: "execute_js"; agentRole: RedlineAgentRole | null };

export const REDLINE_ATTEMPT_OUTCOMES = [
    "applied_clean",
    "recovered_backend_repair",
    "recovered_agent_clean",
    "recovered_agent_drift",
    "range_unresolved",
    "hard_failure",
] as const satisfies readonly RedlineApplyOutcome[];

export type RedlineAttemptOutcome = (typeof REDLINE_ATTEMPT_OUTCOMES)[number];

export function readReviewIdOfApplySource(source: RedlineApplySource): string | null {
    return source.initiatedFrom === "execute_js" ? null : source.reviewId;
}

export function describeApplySourceForWire(source: RedlineApplySource): {
    applyInitiatedFrom: RedlineApplyInitiatedFrom;
    reviewId: string | null;
    agentRole: RedlineAgentRole | null;
} {
    return source.initiatedFrom === "execute_js"
        ? { applyInitiatedFrom: "execute_js", reviewId: null, agentRole: source.agentRole }
        : { applyInitiatedFrom: source.initiatedFrom, reviewId: readReviewIdOfApplySource(source), agentRole: null };
}

export interface RedlineApplyOutcomeCount {
    outcome: RedlineApplyOutcome;
    count: number;
}

export interface RedlineApplyDropCount {
    reason: RedlineDropReason;
    count: number;
}

export interface RedlineApplyOutcomeBatch {
    applyInitiatedFrom: RedlineApplyInitiatedFrom;
    reviewId: string | null;
    agentRole: RedlineAgentRole | null;
    entries: RedlineApplyOutcomeCount[];
    drops: RedlineApplyDropCount[];
    snapshotIds: string[];
}

export async function recordRedlineApplyOutcomes(batch: RedlineApplyOutcomeBatch): Promise<void> {
    const entries = batch.entries.filter(entry => entry.count > 0);
    if (entries.length === 0) {
        return;
    }
    const postable: RedlineApplyOutcomeBatch = { ...batch, entries };
    const applyBatchId = uuidv4();

    const apiKey = getApiKey();
    if (!apiKey) {
        return;
    }

    try {
        await abortRequestAfter(OUTCOME_POST_TIMEOUT_MS, signal =>
            safeFetch(`${getEnv("API_BASE_URL")}${REDLINE_APPLY_OUTCOMES_PATH}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${apiKey}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(encodeApplyOutcomeBatchForWire(postable, applyBatchId)),
                signal,
            }));
    } catch {
        return;
    }
}

